(() => {
  "use strict";

  /* =====================================================
     SELECTORS
  ===================================================== */

  const SELECTORS = {
    header: "[data-digi-club-header]",
    drawer: "[data-digi-club-drawer]",
    overlay: "[data-digi-club-overlay]",

    openButton: "[data-digi-club-menu-open]",
    closeButton: "[data-digi-club-menu-close]",

    navigation: "[data-digi-club-navigation]",
    navigationSection:
      "[data-digi-club-navigation-item]",

    scrollTopButton:
      "[data-digi-club-scroll-top]",

    homeButton:
      "[data-digi-club-home]",

    confirm:
      "[data-digi-club-confirm]",

    confirmAccept:
      "[data-digi-club-confirm-accept]",

    confirmCancel:
      "[data-digi-club-confirm-cancel]",
  };


  /* =====================================================
     CLASSES
  ===================================================== */

  const CLASSES = {
    initialized: "is-initialized",
    menuOpen: "is-menu-open",
    drawerOpen: "is-open",
    overlayVisible: "is-visible",
    confirmVisible: "is-visible",
    active: "is-active",
    complete: "is-complete",
    scrolled: "is-scrolled",
    bodyLocked: "digi-club-menu-open",
  };


  /* =====================================================
     CONFIG
  ===================================================== */

  const CONFIG = {
    transitionDuration: 350,
    scrollOffset: 20,
  };


  /* =====================================================
     HEADER CLASS
  ===================================================== */

  class DigiClubHeader {
    constructor(header) {
      if (!header) return;

      this.header = header;

      this.root =
        header.closest(".shopify-section") ||
        header.parentElement ||
        document;

      this.sections = [];
      this.navigationItems = [];

      this.isDrawerOpen = false;
      this.isConfirmOpen = false;
      this.isScrollTicking = false;

      this.lastFocusedElement = null;
      this.confirmLastFocusedElement = null;

      this.overlayHideTimeout = null;
      this.confirmHideTimeout = null;

      this.cacheElements();
      this.bindMethods();
      this.init();
    }


    /* =====================================================
       CACHE ELEMENTS
    ===================================================== */

    cacheElements() {
      this.drawer =
        this.root.querySelector(
          SELECTORS.drawer
        );

      this.overlay =
        this.root.querySelector(
          SELECTORS.overlay
        );

      this.openButton =
        this.header.querySelector(
          SELECTORS.openButton
        );

      this.closeButton =
        this.drawer?.querySelector(
          SELECTORS.closeButton
        );

      this.navigation =
        this.drawer?.querySelector(
          SELECTORS.navigation
        );

      this.scrollTopButton =
        this.header.querySelector(
          SELECTORS.scrollTopButton
        );

      this.homeButton =
        this.drawer?.querySelector(
          SELECTORS.homeButton
        );

      this.confirmModal =
        this.root.querySelector(
          SELECTORS.confirm
        );

      this.confirmAccept =
        this.confirmModal?.querySelector(
          SELECTORS.confirmAccept
        );

      this.confirmCancel =
        this.confirmModal?.querySelector(
          SELECTORS.confirmCancel
        );
    }


    /* =====================================================
       METHOD BINDINGS
    ===================================================== */

    bindMethods() {
      this.handleOpenClick =
        this.openDrawer.bind(this);

      this.handleCloseClick =
        this.closeDrawer.bind(this);

      this.handleOverlayClick =
        this.onOverlayClick.bind(this);

      this.handleModalBackdropClick =
        this.onModalBackdropClick.bind(this);

      this.handleKeydown =
        this.onKeydown.bind(this);

      this.handleScroll =
        this.onScroll.bind(this);

      this.handleResize =
        this.onResize.bind(this);

      this.handleScrollTop =
        this.scrollToTop.bind(this);

      this.handleHomeClick =
        this.openConfirm.bind(this);

      this.handleConfirmCancel =
        this.closeConfirm.bind(this);

      this.handleConfirmAccept =
        this.confirmHomeNavigation.bind(this);

      this.handleSectionIntersections =
        this.onSectionIntersections.bind(this);
    }


    /* =====================================================
       INITIALIZATION
    ===================================================== */

    init() {
      if (
        this.header.dataset
          .digiClubHeaderInitialized === "true"
      ) {
        return;
      }

      if (
        !this.drawer ||
        !this.overlay ||
        !this.openButton ||
        !this.navigation
      ) {
        console.warn(
          "[Digi Club Header] Estrutura incompleta."
        );

        return;
      }

      this.header.dataset
        .digiClubHeaderInitialized = "true";

      this.openButton.setAttribute(
        "aria-expanded",
        "false"
      );

      this.drawer.setAttribute(
        "aria-hidden",
        "true"
      );

      if (this.confirmModal) {
        this.confirmModal.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      this.collectSections();
      this.buildNavigation();
      this.bindEvents();
      this.setupSectionObserver();

      this.updateHeaderScrollState();
      this.updateActiveFromPosition();

      this.header.classList.add(
        CLASSES.initialized
      );
    }


    /* =====================================================
       EVENTS
    ===================================================== */

    bindEvents() {
      this.openButton.addEventListener(
        "click",
        this.handleOpenClick
      );

      this.closeButton?.addEventListener(
        "click",
        this.handleCloseClick
      );

      this.overlay.addEventListener(
        "click",
        this.handleOverlayClick
      );

      this.confirmModal?.addEventListener(
        "click",
        this.handleModalBackdropClick
      );

      this.scrollTopButton?.addEventListener(
        "click",
        this.handleScrollTop
      );

      this.homeButton?.addEventListener(
        "click",
        this.handleHomeClick
      );

      this.confirmCancel?.addEventListener(
        "click",
        this.handleConfirmCancel
      );

      this.confirmAccept?.addEventListener(
        "click",
        this.handleConfirmAccept
      );

      window.addEventListener(
        "scroll",
        this.handleScroll,
        {
          passive: true,
        }
      );

      window.addEventListener(
        "resize",
        this.handleResize,
        {
          passive: true,
        }
      );

      document.addEventListener(
        "keydown",
        this.handleKeydown
      );
    }


    /* =====================================================
       DRAWER
    ===================================================== */

    openDrawer() {
      if (this.isDrawerOpen) return;

      this.isDrawerOpen = true;

      this.lastFocusedElement =
        document.activeElement;

      this.header.classList.add(
        CLASSES.menuOpen
      );

      this.drawer.classList.add(
        CLASSES.drawerOpen
      );

      this.drawer.setAttribute(
        "aria-hidden",
        "false"
      );

      this.openButton.setAttribute(
        "aria-expanded",
        "true"
      );

      this.updateInterfaceState();

      window.requestAnimationFrame(() => {
        this.closeButton?.focus();
      });
    }


    closeDrawer(options = {}) {
      if (!this.isDrawerOpen) return;

      const {
        restoreFocus = true,
      } = options;

      this.isDrawerOpen = false;

      this.header.classList.remove(
        CLASSES.menuOpen
      );

      this.drawer.classList.remove(
        CLASSES.drawerOpen
      );

      this.drawer.setAttribute(
        "aria-hidden",
        "true"
      );

      this.openButton.setAttribute(
        "aria-expanded",
        "false"
      );

      this.updateInterfaceState();

      if (
        restoreFocus &&
        !this.isConfirmOpen &&
        this.lastFocusedElement instanceof
          HTMLElement
      ) {
        window.requestAnimationFrame(() => {
          this.lastFocusedElement.focus();
        });
      }
    }


    onOverlayClick() {
      if (this.isConfirmOpen) {
        this.closeConfirm();
        return;
      }

      if (this.isDrawerOpen) {
        this.closeDrawer();
      }
    }


    /* =====================================================
       CONFIRM MODAL
    ===================================================== */

    openConfirm() {
      if (
        !this.confirmModal ||
        this.isConfirmOpen
      ) {
        return;
      }

      this.isConfirmOpen = true;

      this.confirmLastFocusedElement =
        document.activeElement;

      window.clearTimeout(
        this.confirmHideTimeout
      );

      this.confirmModal.hidden = false;

      this.confirmModal.setAttribute(
        "aria-hidden",
        "false"
      );

      this.updateInterfaceState();

      window.requestAnimationFrame(() => {
        this.confirmModal.classList.add(
          CLASSES.confirmVisible
        );

        this.confirmCancel?.focus();
      });
    }


    closeConfirm(options = {}) {
      if (
        !this.confirmModal ||
        !this.isConfirmOpen
      ) {
        return;
      }

      const {
        restoreFocus = true,
      } = options;

      this.isConfirmOpen = false;

      this.confirmModal.classList.remove(
        CLASSES.confirmVisible
      );

      this.confirmModal.setAttribute(
        "aria-hidden",
        "true"
      );

      window.clearTimeout(
        this.confirmHideTimeout
      );

      this.confirmHideTimeout =
        window.setTimeout(() => {
          if (!this.isConfirmOpen) {
            this.confirmModal.hidden = true;
          }
        }, CONFIG.transitionDuration);

      this.updateInterfaceState();

      if (
        restoreFocus &&
        this.confirmLastFocusedElement instanceof
          HTMLElement
      ) {
        window.requestAnimationFrame(() => {
          this.confirmLastFocusedElement.focus();
        });
      }
    }


    onModalBackdropClick(event) {
      if (
        event.target !==
        this.confirmModal
      ) {
        return;
      }

      this.closeConfirm();
    }


    async confirmHomeNavigation() {
      if (!this.homeButton) return;

      const homeUrl =
        this.homeButton.dataset.homeUrl ||
        "/";

      this.closeConfirm({
        restoreFocus: false,
      });

      await this.clearCartAndRedirect(
        homeUrl
      );
    }


    /* =====================================================
       SHARED OVERLAY + BODY LOCK
    ===================================================== */

    updateInterfaceState() {
      const shouldShowOverlay =
        this.isDrawerOpen ||
        this.isConfirmOpen;

      const shouldLockBody =
        this.isDrawerOpen ||
        this.isConfirmOpen;

      window.clearTimeout(
        this.overlayHideTimeout
      );

      if (shouldShowOverlay) {
        this.overlay.hidden = false;

        window.requestAnimationFrame(() => {
          this.overlay.classList.add(
            CLASSES.overlayVisible
          );
        });
      } else {
        this.overlay.classList.remove(
          CLASSES.overlayVisible
        );

        this.overlayHideTimeout =
          window.setTimeout(() => {
            if (
              !this.isDrawerOpen &&
              !this.isConfirmOpen
            ) {
              this.overlay.hidden = true;
            }
          }, CONFIG.transitionDuration);
      }

      document.body.classList.toggle(
        CLASSES.bodyLocked,
        shouldLockBody
      );
    }


    /* =====================================================
       KEYBOARD
    ===================================================== */

    onKeydown(event) {
      if (event.key === "Escape") {
        if (this.isConfirmOpen) {
          event.preventDefault();
          this.closeConfirm();
          return;
        }

        if (this.isDrawerOpen) {
          event.preventDefault();
          this.closeDrawer();
        }

        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      if (this.isConfirmOpen) {
        this.trapFocus(
          event,
          this.confirmModal
        );

        return;
      }

      if (this.isDrawerOpen) {
        this.trapFocus(
          event,
          this.drawer
        );
      }
    }


    trapFocus(event, container) {
      if (!container) return;

      const focusableElements =
        container.querySelectorAll(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(",")
        );

      const focusable =
        Array.from(
          focusableElements
        ).filter((element) => {
          return (
            !element.hidden &&
            element.getAttribute(
              "aria-hidden"
            ) !== "true" &&
            element.offsetParent !== null
          );
        });

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement =
        focusable[0];

      const lastElement =
        focusable[
          focusable.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement ===
          firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement ===
          lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }


    /* =====================================================
       NAVIGATION COLLECTION
    ===================================================== */

    collectSections() {
      const sections = Array.from(
        document.querySelectorAll(
          SELECTORS.navigationSection
        )
      );

      this.sections =
        sections.filter((section) => {
          const label =
            section.dataset
              .digiClubNavigationLabel
              ?.trim();

          return Boolean(
            section.id &&
            label
          );
        });
    }


    /* =====================================================
       NAVIGATION BUILD
    ===================================================== */

    buildNavigation() {
      this.navigation.innerHTML = "";
      this.navigationItems = [];

      if (this.sections.length === 0) {
        this.renderEmptyNavigation();
        return;
      }

      const fragment =
        document.createDocumentFragment();

      this.sections.forEach(
        (section, index) => {
          const item =
            this.createNavigationItem(
              section,
              index
            );

          fragment.appendChild(item);
        }
      );

      this.navigation.appendChild(
        fragment
      );
    }


    createNavigationItem(
      section,
      index
    ) {
      const label =
        section.dataset
          .digiClubNavigationLabel
          .trim();

      const listItem =
        document.createElement("li");

      listItem.className =
        "digi-club-header__navigation-item";

      listItem.dataset
        .digiClubNavigationIndex =
          String(index);

      const button =
        document.createElement("button");

      button.type = "button";

      button.className =
        "digi-club-header__navigation-button";

      button.dataset
        .digiClubNavigationTarget =
          section.id;

      button.setAttribute(
        "aria-label",
        `Ir para ${label}`
      );

      const status =
        document.createElement("span");

      status.className =
        "digi-club-header__navigation-status";

      status.setAttribute(
        "aria-hidden",
        "true"
      );

      const dot =
        document.createElement("span");

      dot.className =
        "digi-club-header__navigation-dot";

      dot.innerHTML = `
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path
            d="M3.5 8.2L6.7 11.2L12.5 5.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      `;

      const labelElement =
        document.createElement("span");

      labelElement.className =
        "digi-club-header__navigation-label";

      labelElement.textContent =
        label;

      status.appendChild(dot);

      button.append(
        status,
        labelElement
      );

      button.addEventListener(
        "click",
        () => {
          this.handleNavigationClick(
            section,
            index
          );
        }
      );

      listItem.appendChild(button);

      this.navigationItems.push({
        element: listItem,
        button,
        section,
        index,
      });

      return listItem;
    }


    renderEmptyNavigation() {
      const item =
        document.createElement("li");

      item.className =
        "digi-club-header__navigation-empty";

      item.textContent =
        "Nenhuma seção disponível.";

      this.navigation.appendChild(
        item
      );
    }


    /* =====================================================
       NAVIGATION ACTIONS
    ===================================================== */

    handleNavigationClick(
      section,
      index
    ) {
      this.closeDrawer({
        restoreFocus: false,
      });

      window.requestAnimationFrame(() => {
        this.scrollToSection(
          section,
          index
        );
      });
    }


    scrollToSection(
      section,
      index
    ) {
      if (!section) return;

      const headerHeight =
        this.header.offsetHeight || 0;

      const targetTop =
        window.scrollY +
        section.getBoundingClientRect().top -
        headerHeight -
        CONFIG.scrollOffset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });

      this.updateUrlHash(
        section.id
      );

      this.updateProgressStates(
        index
      );
    }


    scrollToTop() {
      if (this.isDrawerOpen) {
        this.closeDrawer({
          restoreFocus: false,
        });
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      if (
        this.navigationItems.length > 0
      ) {
        this.updateProgressStates(0);
      }

      this.removeUrlHash();
    }


    /* =====================================================
       ACTIVE SECTION
    ===================================================== */

    updateActiveFromPosition() {
      if (this.sections.length === 0) {
        return;
      }

      const headerOffset =
        this.header.offsetHeight +
        CONFIG.scrollOffset +
        12;

      let activeIndex = 0;

      this.sections.forEach(
        (section, index) => {
          const rect =
            section.getBoundingClientRect();

          if (
            rect.top <= headerOffset
          ) {
            activeIndex = index;
          }
        }
      );

      this.updateProgressStates(
        activeIndex
      );
    }


    updateProgressStates(
      activeIndex
    ) {
      this.navigationItems.forEach(
        (item) => {
          const isActive =
            item.index === activeIndex;

          const isComplete =
            item.index < activeIndex;

          item.element.classList.toggle(
            CLASSES.active,
            isActive
          );

          item.element.classList.toggle(
            CLASSES.complete,
            isComplete
          );

          if (isActive) {
            item.button.setAttribute(
              "aria-current",
              "location"
            );
          } else {
            item.button.removeAttribute(
              "aria-current"
            );
          }
        }
      );
    }


    /* =====================================================
       INTERSECTION OBSERVER
    ===================================================== */

    setupSectionObserver() {
      this.sectionObserver?.disconnect();

      if (
        !("IntersectionObserver" in window) ||
        this.sections.length === 0
      ) {
        return;
      }

      this.sectionObserver =
        new IntersectionObserver(
          this.handleSectionIntersections,
          {
            root: null,
            rootMargin:
              "-88px 0px -45% 0px",

            threshold: [
              0,
              0.1,
              0.25,
              0.5,
              0.75,
            ],
          }
        );

      this.sections.forEach(
        (section) => {
          this.sectionObserver.observe(
            section
          );
        }
      );
    }


    onSectionIntersections(
      entries
    ) {
      const visibleEntries =
        entries.filter(
          (entry) =>
            entry.isIntersecting
        );

      if (
        visibleEntries.length === 0
      ) {
        return;
      }

      visibleEntries.sort(
        (entryA, entryB) => {
          const distanceA =
            Math.abs(
              entryA.boundingClientRect.top
            );

          const distanceB =
            Math.abs(
              entryB.boundingClientRect.top
            );

          return distanceA - distanceB;
        }
      );

      const activeSection =
        visibleEntries[0].target;

      const activeIndex =
        this.sections.indexOf(
          activeSection
        );

      if (activeIndex < 0) return;

      this.updateProgressStates(
        activeIndex
      );
    }


    /* =====================================================
       WINDOW SCROLL
    ===================================================== */

    onScroll() {
      if (this.isScrollTicking) {
        return;
      }

      this.isScrollTicking = true;

      window.requestAnimationFrame(() => {
        this.updateHeaderScrollState();
        this.updateActiveFromPosition();

        this.isScrollTicking = false;
      });
    }


    updateHeaderScrollState() {
      const isScrolled =
        window.scrollY > 12;

      this.header.classList.toggle(
        CLASSES.scrolled,
        isScrolled
      );
    }


    onResize() {
      this.setupSectionObserver();
      this.updateActiveFromPosition();
    }


    /* =====================================================
       URL HASH
    ===================================================== */

    updateUrlHash(sectionId) {
      if (
        !window.history ||
        !window.history.replaceState
      ) {
        return;
      }

      const url =
        new URL(
          window.location.href
        );

      url.hash = sectionId;

      window.history.replaceState(
        {},
        "",
        url
      );
    }


    removeUrlHash() {
      if (
        !window.history ||
        !window.history.replaceState
      ) {
        return;
      }

      const url =
        new URL(
          window.location.href
        );

      url.hash = "";

      window.history.replaceState(
        {},
        "",
        url.pathname +
          url.search
      );
    }


    /* =====================================================
       CART CLEAR + REDIRECT
    ===================================================== */

    async clearCartAndRedirect(
      homeUrl
    ) {
      if (!this.homeButton) return;

      const originalLabel =
        this.homeButton.textContent;

      this.homeButton.disabled = true;

      this.homeButton.textContent =
        "Limpando carrinho...";

      try {
        const shopifyRoot =
          window.Shopify?.routes?.root ||
          "/";

        const clearCartUrl =
          `${shopifyRoot}cart/clear.js`;

        const response =
          await fetch(
            clearCartUrl,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Accept:
                  "application/json",
              },
            }
          );

        if (!response.ok) {
          throw new Error(
            `Falha ao limpar o carrinho: ${response.status}`
          );
        }

        document.documentElement
          .dispatchEvent(
            new CustomEvent(
              "cart:refresh",
              {
                bubbles: true,
              }
            )
          );

        window.location.assign(
          homeUrl
        );
      } catch (error) {
        console.error(
          "[Digi Club Header] Não foi possível limpar o carrinho.",
          error
        );

        window.alert(
          "Não foi possível limpar o carrinho. Tente novamente."
        );

        this.homeButton.disabled = false;

        this.homeButton.textContent =
          originalLabel;
      }
    }


    /* =====================================================
       REFRESH
    ===================================================== */

    refreshNavigation() {
      this.sectionObserver?.disconnect();

      this.collectSections();
      this.buildNavigation();
      this.setupSectionObserver();
      this.updateActiveFromPosition();
    }


    /* =====================================================
       DESTROY
    ===================================================== */

    destroy() {
      this.sectionObserver?.disconnect();

      window.clearTimeout(
        this.overlayHideTimeout
      );

      window.clearTimeout(
        this.confirmHideTimeout
      );

      this.openButton?.removeEventListener(
        "click",
        this.handleOpenClick
      );

      this.closeButton?.removeEventListener(
        "click",
        this.handleCloseClick
      );

      this.overlay?.removeEventListener(
        "click",
        this.handleOverlayClick
      );

      this.confirmModal?.removeEventListener(
        "click",
        this.handleModalBackdropClick
      );

      this.scrollTopButton?.removeEventListener(
        "click",
        this.handleScrollTop
      );

      this.homeButton?.removeEventListener(
        "click",
        this.handleHomeClick
      );

      this.confirmCancel?.removeEventListener(
        "click",
        this.handleConfirmCancel
      );

      this.confirmAccept?.removeEventListener(
        "click",
        this.handleConfirmAccept
      );

      window.removeEventListener(
        "scroll",
        this.handleScroll
      );

      window.removeEventListener(
        "resize",
        this.handleResize
      );

      document.removeEventListener(
        "keydown",
        this.handleKeydown
      );

      document.body.classList.remove(
        CLASSES.bodyLocked
      );

      this.overlay?.classList.remove(
        CLASSES.overlayVisible
      );

      if (this.overlay) {
        this.overlay.hidden = true;
      }

      this.confirmModal?.classList.remove(
        CLASSES.confirmVisible
      );

      if (this.confirmModal) {
        this.confirmModal.hidden = true;

        this.confirmModal.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      this.drawer?.classList.remove(
        CLASSES.drawerOpen
      );

      this.header.classList.remove(
        CLASSES.menuOpen
      );

      this.header.removeAttribute(
        "data-digi-club-header-initialized"
      );

      delete this.header
        .digiClubHeaderInstance;
    }
  }


  /* =====================================================
     INITIALIZATION
  ===================================================== */

  const initializeHeaders = (
    scope = document
  ) => {
    const headers = [];

    if (
      scope instanceof Element &&
      scope.matches(
        SELECTORS.header
      )
    ) {
      headers.push(scope);
    }

    scope
      .querySelectorAll?.(
        SELECTORS.header
      )
      .forEach((header) => {
        headers.push(header);
      });

    headers.forEach((header) => {
      if (
        header.digiClubHeaderInstance
      ) {
        return;
      }

      const instance =
        new DigiClubHeader(header);

      if (
        header.dataset
          .digiClubHeaderInitialized !==
        "true"
      ) {
        return;
      }

      header.digiClubHeaderInstance =
        instance;
    });
  };


  const refreshHeaders = () => {
    document
      .querySelectorAll(
        SELECTORS.header
      )
      .forEach((header) => {
        header
          .digiClubHeaderInstance
          ?.refreshNavigation();
      });
  };


  /* =====================================================
     SHOPIFY THEME EDITOR
  ===================================================== */

  const handleSectionLoad = (
    event
  ) => {
    initializeHeaders(
      event.target
    );

    window.requestAnimationFrame(
      refreshHeaders
    );
  };


  const handleSectionUnload = (
    event
  ) => {
    const header =
      event.target.matches?.(
        SELECTORS.header
      )
        ? event.target
        : event.target.querySelector?.(
            SELECTORS.header
          );

    header
      ?.digiClubHeaderInstance
      ?.destroy();

    window.requestAnimationFrame(
      refreshHeaders
    );
  };


  /* =====================================================
     START
  ===================================================== */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeHeaders();
      },
      {
        once: true,
      }
    );
  } else {
    initializeHeaders();
  }


  document.addEventListener(
    "shopify:section:load",
    handleSectionLoad
  );


  document.addEventListener(
    "shopify:section:unload",
    handleSectionUnload
  );


  document.addEventListener(
    "shopify:section:reorder",
    () => {
      window.requestAnimationFrame(
        refreshHeaders
      );
    }
  );
})();